import { IEditorProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import { IEditorServices } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/interface/editor-service.interface';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';
import ResizeMeasure from '@src/slide/domain/ui-model/element-modeles/property/text/editor-measure/resize-measure';
import { Selection, DocumentElement, ParagraphElement, TextElement, Platform } from '@tencent/alloy-editor';
import DocsEditor from '@tencent/docs-editor';
import { LinkText, textElementToTextNode } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/render/link-text-node';
import { DEFAULT_TAIL_WHITESPACE_CALCULATION, FONT_FILL_KEY } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { IAllStyles, IBodyStyle, IParagraphStyle, ITextBodyStyle, ITextRunStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import stylesMerge from '@src/slide/domain/ui-model/element-modeles/property/text/utils/style-merge';
import DataTransform from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model/data-transform';
import { FONT_SCALE } from '@editor/src/page/index/components/editor/config';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { hasBullet } from '@editor/src/page/index/components/editor/data/character';
import { getDefaultRect, isEditorEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { isSameObj } from '@src/js/utils';
import { ISelection, ParaRunStyles, EDITOR_DEFAULT_HEIGHT, EDITOR_DEFAULT_WIDTH, IRect, ITextRect } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import { IParagraph, ISelectionParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { WrapType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { getLogicPropertyKeyMap } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-direction';
import { LinkHandler } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-event/link-handler';
import { InteractionEvent } from '@tencent/alloy-renderer';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { TEXT_CURSOR, VERTICAL_TEXT_CURSOR, DEFAULT_FONT_FAMILY, SPECIFIC_DEFAULT_FONT_FAMILY } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/editor-constants';
import { ITextEditor, ITextEditorEventHandlers } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';
import { ComponentTypes } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/component-types';
import { IBaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/base-component.interface';
import { transformParagraphForCharacter } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils';
import { createCharacterProps } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model/character-transform';
import { CANVAS_RECT_TEXT_ID, CANVAS_TEXT_EDITOR_ID_PREFIX, CANVAS_TEXT_IMAGE_ID } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';

const DEFAULT_LINK_COLOR = '#0563C1';

/**
 * Editor容器，用于承载Editor以及响应Editor的回调
 */
export class TextEditor implements ITextEditor, IBaseComponent {
    public editorModel: EditorDataModel;
    /**
     * 外部传入参数列表
    */
    public props: IEditorProps;
    public editorContainer: DocsEditor;
    public services: IEditorServices;
    private valueUpdated = false;
    private rectUpdated = false;
    private eventHandlers?: ITextEditorEventHandlers;
    private id: string;
    private linkHandler: LinkHandler;
    private flag: boolean;
    public constructor(editorProps: IEditorProps, services: IEditorServices) {
        this.id = `${CANVAS_TEXT_EDITOR_ID_PREFIX}-${editorProps.modelKey}-${editorProps.modelKeySuffix}`;
        this.props = editorProps;
        this.services = services;
        const lazyLoadEditor = isEditorEmpty(editorProps.paragraphList || []);
        this.linkHandler = this.generateLinkHandler();
        this.initEditorData();
        this.editorContainer = new DocsEditor(this.getEditorProps(this.props), lazyLoadEditor);
        this.services.logicService.setComponentLogic({}, this);
    }

    public setEventHandlers(eventHandlers: ITextEditorEventHandlers) {
        this.eventHandlers = eventHandlers;
    }

    public get isEditing(): boolean {
        return !!this.controller?.isEditing;
    }

    // 可输入文字但是不显示光标
    public get isReadyEditing(): boolean {
        return !!this.controller?.isReadyEditing;
    }

    public get isTextEnterable(): boolean {
        return !!this.controller?.isTextEnterable;
    }

    public get changeFlag(): boolean {
        return !!this.controller?.changeFlag;
    }

    public get readonly(): boolean {
        return !!this.editor?.readonly;
    }

    /**
     * 添加查找替换当前高亮文本信息
     * @param result
     * @param keyword
     */
    public addHighlight = (result, keyword: string): void => {
        this.controller?.addHighlight(result, keyword);
    };

    /**
     * 添加查找到的文本信息
     * @param result
     * @param keyword
     */
    public addSearchMark = (result, keyword: string): void => {
        this.controller?.addSearchMark(result, keyword);
    };

    /**
     * 移除查找信息
     */
    public removeHighlight = (): void => {
        this.controller?.removeHighlight();
    };

    /**
     * 移除高亮信息
     */
    public removeSearchMark = (): void => {
        this.controller?.removeSearchMark();
    };

    public copy = () => this.controller?.copy();

    public paste = (textBody, keepProps = true) => this.controller?.paste(textBody, keepProps);

    public cut = () => this.controller?.cut();

    public emptyParagraph = (): void => {
        this.controller?.emptyParagraph();
    };

    public getExpandedRect = () => this.controller?.getExpandedRect();

    public getSelectionPara = (): Array<ISelectionParagraph> => this.controller?.getSelectionPara() || [];

    public getSelection = (needConvertOffset?: boolean): ISelection => this.controller?.getSelection(needConvertOffset)
        || null;


    public setFocus = (callBack?: () => void, event?: InteractionEvent): void => {
        if (this.editor?.readonly) return;  // 只读态，直接返回
        if (!this.editor) {
            this.editorContainer?.initEditor();
        }
        this.controller?.setFocus(callBack, event);
    };

    public setReadyEditing = (): void => {
        if (this.editor?.readonly) return;  // 只读态，直接返回
        if (!this.editor) {
            this.editorContainer.initEditor();
        }
        this.controller?.setReadyEditing();
    };

    public triggerBlur = (): void => {
        this.controller?.triggerBlur();
    };

    public operations = (key: string, value, option): boolean => {
        if (!this.editor) {
            this.editorContainer.initEditor();
        }
        return !!this.controller?.operations(key, value, option);
    };


    /**
     * props更新，检查是否需要刷新
     * @param editorProps
     */
    public updateEditorProps(editorProps: IEditorProps): void {
        this.props = editorProps; // 更新props
        this.linkHandler = this.generateLinkHandler();
        this.editorContainer.updateProps(this.getEditorProps(editorProps));
        if (this.shouldEditorUpdate(editorProps)) {
            if (this.valueUpdated) {
                const editorValue = this.getEditorValue();
                this.editorContainer?.resetValue(editorValue, this.rectUpdated);
                this.props.onStyleChange?.({
                    style: this.getAllStylesSync(),
                });
            } else if (this.rectUpdated) { // valueUpdated会处理rectChange，所以这里是互斥逻辑
                this.editorContainer?.changeRect();
            }
        }

        // props变更时更新 readOnly 属性
        if (this.readonly !== this.isReadOnly()) {
            this.updateReadonlyState(this.isReadOnly());
        }

        // 更新鼠标样式
        this.editorContainer.updateCursor(this.getCursor(this.props.cursor));
    }

    public updateCursor = (cursor: string = TEXT_CURSOR) => {
        this.editorContainer.updateCursor(this.getCursor(cursor));
    };

    public getTextAreaRectSync = (): {
        width: number,
        height: number,
    } => this.editor?.getLayoutSize() || { width: 0, height: 0 };

    public getAllStyles = (): IAllStyles => this.getAllStylesSync();

    public getAllStylesSync = (): IAllStyles => {
        const { textBodyStyle, paraStyleList } = this.collectStyles();
        return this.mergeStyles(paraStyleList, textBodyStyle);
    };

    /**
     * 是否只读，默认为false
     */
    public isReadOnly = (): boolean => {
        if (typeof(this.props.editable) !== 'undefined') {
            return !this.props.editable;
        }
        return false;
    };

    /**
     * 是否可选择文字，默认为 false
     * 可编辑态直接返回 true
     */
    public isSelectable = (): boolean => {
        // 可编辑态直接返回 true
        if (!this.isReadOnly()) {
            return true;
        }
        if (typeof(this.props.selectable) !== 'undefined') {
            return this.props.selectable;
        }
        return false;
    };

    public getData = (): { paragraphList: IParagraph[]; textBodyStyle?: ITextBodyStyle } =>
        this.getEditorData();

    public getEditorHeightByParagraphList = (paragraphList: IParagraph[], bodyStyle?: IBodyStyle) => {
        const textRect = this.props.textRect ? { ...this.props.textRect } : getDefaultRect();
        const textBodyStyle = {
            ...this.props.textBodyStyle,
        };
        if (bodyStyle) {
            textBodyStyle.bodyStyle = bodyStyle;
        }

        const documentValue = EditorDataModel.convertFromTextModel(
            { paragraphList, textBodyStyle },
            { noConvertBack: true },
        );

        let inLineSize: number|undefined;
        if (textBodyStyle.bodyStyle?.whiteSpace === WrapType.NO_WRAP) {
            inLineSize = ResizeMeasure.getEditorInlineSize(documentValue, { textRect, textBodyStyle });
        }

        const { textDirection } = this.props.textBodyStyle?.wrapperStyle || {};
        const logicKeys = getLogicPropertyKeyMap(textDirection);

        textRect[logicKeys.inlineSize] = inLineSize || textRect?.[logicKeys.inlineSize];
        const blockSize = ResizeMeasure.getEditorBlockSize(documentValue, { textRect, textBodyStyle });

        return blockSize;
    };

    public getResizeLevel = (): number | undefined => this.editorModel.resizeLevel;

    /**
     * 重新用props数据刷新editor-model并触发editor刷新
     */
    public refreshEditorByProps = (): void => {
        this.editorModel.setRawData({
            paragraphList: this.props.paragraphList,
            textBodyStyle: this.props.textBodyStyle,
            resizeLevel: ResizeMeasure.getResizeLevel(this.props),
            slideNum: this.props.slideNum,
            datetime: this.props.datetime,
            model: this.props.model,
        }, true, true);
    };

    public unregisterEvent = () => {
        this.eventHandlers?.unregisterEvent();
    };

    public dispose = () => {
        this.eventHandlers?.unregisterEvent();
        this.services.logicService.removeComponentLogic(this);
        this.controller?.dispose();
        if (this.props.modelKey) {
            this.services.fontManager?.disposeFontDownloadCallbackByShapeModelKey(
                this.props.modelKey,
                this.onFontDownloadedCallback,
            );
        }
    };

    public getTextAreaHeightSync = () => this.editorContainer?.getLayoutSize()?.height || 0;

    /**
     * 只读状态触发click，否则触发setFoucs
     * @param callBack
     */
    public setFocusOrClick = (callBack?: () => void): void => {
        if (!this.readonly) {
            this.setFocus(callBack);
        } else {
            this.eventHandlers?.handleClick();
            callBack?.();
        }
    };

    public getTextLinkState = (): boolean => !!this.controller?.getTextLinkState();

    public getBackDeleteTextProps = (): string[] => this.controller?.getBackDeleteTextProps() || [];

    public insertText = (text, styles): void => {
        this.controller?.insertText(text, styles);
    };

    public insertTextWithNoAutoSave = (text, styles): void => {
        this.controller?.insertTextWithNoAutoSave(text, styles);
    };

    public insertTextWithSaveNow = (text, styles): void => {
        this.controller?.insertTextWithSaveNow(text, styles);
    };

    public insertTextWithSaveNowAndExitEditing = (text, styles): void => {
        this.controller?.insertTextWithSaveNowAndExitEditing(text, styles);
    };

    public isExpanded = (): boolean => this.editor?.isSelectionExpanded?.() || false;

    public setSelectionAll = (): void => this.controller?.setSelectionAll();

    public setTextLink = (): boolean => !!this.controller?.setTextLink();

    public triggerRedo = (): void => {
        this.controller?.triggerRedo();
    };

    public triggerUndo = (): void => {
        this.controller?.triggerUndo();
    };

    public setSelectionToEnd = (): void => {
        this.controller?.setSelectionToEnd();
    };

    public triggerSave = (): void => {
        this.controller?.triggerSave();
    };

    /**
     * 重置「文本是否有改变」的标记，在外界触发文字保存之后执行，避免重复保存
     */
    public resetValueChangeFlag = (): void => {
        this.controller?.resetValueChangeFlag();
    };

    public updateReadonlyState = (isReadOnly: boolean) => {
        if (isReadOnly) {  // 从可编辑变为不可编辑, 执行一些清理操作
            this.controller?.clear();
        }
        this.editor?.changeReadonly(isReadOnly);
        // 更新只读状态时需要同步更新注册事件
        this.eventHandlers?.unregisterEvent();
        this.eventHandlers?.registerEvent();
    };

    public updateEditorData = () => {
        this.controller?.updateEditorData();
    };

    /**
     * 如果当前元素位置变化了，更新下光标位置
     * 如果命中缓存的话，可能出现当前组件没重新渲染，但是 worldTransform 变化的情况
     * 所以只在当前组件 onDidRender 更新可能是不全面的
     * 暴露这个方法是为了在外界可以更新当前正在编辑文本框的光标位置
     */
    public ifUpdateEditorSurface = (): void => {
        this.editorContainer.ifUpdateEditorSurface();
    };

    public get editor() {
        return this.editorContainer?.editor;
    }

    public onChangeFlagChange = (flag: boolean) => {
        if (this.services.editingStateManager) {
            this.services.editingStateManager.hasUnsavedTextContent = flag;
        }
    };

    public getUniqueId(): string {
        return this.id;
    }

    public getComponentType(): string {
        return ComponentTypes.TEXTEDITOR;
    }

    public onFontDownloadedCallback = (downloadedFont) => {
        this.editorContainer.invalidateMeasureCache(downloadedFont);
        this.editor.forceRender();
    };

    /**
     * 更新选区并 focus
     * @param selection
     * @param needConvertOffset 是否需要转换把基于 paragraph 的 offset 转换为基于 textRun 的 offset
     */
    public updateSelection = (selection: ISelection, needConvertOffset?: boolean) => {
        if (selection) {
            const textRunSelection = needConvertOffset ? this.editor?.rangeUtil.convertToText(selection) : selection;
            this.editor?.setSelection(textRunSelection);
            this.editor?.focus();
        }
    };

    public getAppPosition = () => {
        const app = this.props?.getApplication?.();
        if (!app) {
            return { x: 0, y: 0 };
        }
        const canvas = app?.view?.native as HTMLCanvasElement;
        if (!canvas) {
            return { x: 0, y: 0 };
        }
        const { left, top } = canvas?.getBoundingClientRect?.() ?? { left: 0, top: 0 };
        return {
            x: left, y: top,
        };
    };

    public getRenderSize = () => this.editorContainer.getRenderSize();

    /**
     * 判断是否需要更新editor相关数据并重新渲染
     * @param nextProps
     */
    private shouldEditorUpdate(nextProps: IEditorProps): boolean {
        let shouldUpdate = false;
        this.valueUpdated = false;
        this.rectUpdated = false;
        // 数据变更，更新数据
        if (nextProps.shouldUpdate) {
            shouldUpdate = this.editorModel.setRawData({
                paragraphList: nextProps.paragraphList,
                textBodyStyle: nextProps.textBodyStyle,
                resizeLevel: ResizeMeasure.getResizeLevel(nextProps),
                slideNum: this.props.slideNum,
                datetime: this.props.datetime,
                model: this.props.model,
                linkThemeColor: nextProps.linkColor,
            }, false);
            this.valueUpdated = shouldUpdate;
            // 重置状态
            nextProps.resetShouldUpdate?.();
        } else if (nextProps.resize) {
            shouldUpdate = this.editorModel.setRawData({
                resizeLevel: ResizeMeasure.getResizeLevel(nextProps, false),
            });
            this.valueUpdated = shouldUpdate;
            this.props.setResize?.(false);
        }
        const rect = this.getTextRect(nextProps.textRect);
        if (!isSameObj(rect, this.editorContainer.textRect)) {
            this.editorContainer.updateTextRect(rect);
            shouldUpdate = true;
            this.rectUpdated = true;
        }
        return shouldUpdate;
    }

    /**
     * 获取文本默认显示区域，单位px
     */
    private getTextRect = (textRect?: ITextRect): IRect => {
        if (textRect) {
            return { x: textRect.left, y: textRect.top, width: textRect.width, height: textRect.height };
        }
        return { x: 0, y: 0, width: EDITOR_DEFAULT_WIDTH, height: EDITOR_DEFAULT_HEIGHT };
    };

    private generateLinkHandler = () => new LinkHandler({
        onLinkMouseEnter: this.handleLinkMouseEnter,
        onLinkMouseLeave: this.handleLinkMouseLeave,
        getLinkColor: this.getLinkColor,
        ...(browser.isMobile
            ? {
                onLinkTouchEnd: this.onLinkTouchEnd,
            }
            : {
                onLinkClick: this.onLinkClick,
            }),
        linkCursor: this.props.linkCursor,
        getTextDirection: () => this.editorModel.textDirection,
    });

    private initEditorData() {
        this.editorModel = new EditorDataModel({
            paragraphList: this.props.paragraphList,
            textBodyStyle: deepCopy(this.props.textBodyStyle),
            resizeLevel: ResizeMeasure.getResizeLevel(this.props),
            slideNum: this.props.slideNum,
            datetime: this.props.datetime,
            noSaveRawData: true, // 查看态不存rawData
            model: this.props.model,
            triggerUpdate: (documentElement: DocumentElement, rawDataChange?: boolean) => {
                const editorValue = this.processEditorValue(documentElement);
                if (rawDataChange) {
                    const oldValue = this.editorContainer.getEditorValue() as DocumentElement;
                    // 只同步数据，不触发排版和绘制
                    this.editorContainer.resetValueWithoutEffect(editorValue);
                    const newValue = this.editorContainer.getEditorValue() as DocumentElement;
                    textElementToTextNode.migrate(oldValue, newValue);
                } else {
                    this.editorContainer?.resetValue(editorValue);
                }
            },
        });
    }

    /**
     * 对生成的通用 editor 数据再做处理
     */
    private processEditorValue(documentElement: DocumentElement): DocumentElement {
        return {
            ...documentElement,
            tailWhitespaceCalculation: DEFAULT_TAIL_WHITESPACE_CALCULATION,
        };
    }

    private getEditorValue(): DocumentElement {
        const documentElement = this.editorModel.getEditorValue();
        return this.processEditorValue(documentElement);
    }

    private collectStyles = () => {
        if (this.isEditing) {
            const paraStyleList: ParaRunStyles[] = [];
            const selPropsArray: any[] = this.editorContainer.getSelectionProps();
            selPropsArray?.length && selPropsArray.forEach(({ paragraphProps, textProps }) => {
                const paraStyle = DataTransform.getOriginProps(paragraphProps, 'raw-') as IParagraphStyle;
                const runStyleList: ITextRunStyle[] = [];
                textProps?.length && textProps.forEach((textElement) => {
                    const textRunStyle = DataTransform.getOriginProps(textElement, 'raw-');
                    runStyleList.push(textRunStyle);
                });
                paraStyleList.push({ paraStyle, runStyleList });
            });
            const textBodyStyle = EditorDataModel.convertTextBodyStyle(this.editorContainer.getEditorValue(), false);
            return { paraStyleList, textBodyStyle };
        }
        const editorValue = this.editorContainer?.getEditorValue() || this.editorModel.getEditorValue();
        const pList = editorValue.children || [];
        const paraStyleList: ParaRunStyles[] = pList.map((paraElement: ParagraphElement) => {
            const paraStyle = DataTransform.getOriginProps(paraElement, 'raw') as IParagraphStyle;
            const textRuns: TextElement[] = paraElement.children || [];
            const runStyleList: ITextRunStyle[] = textRuns.map((textElement: TextElement) => DataTransform.getOriginProps(textElement, 'raw-'));
            return { paraStyle, runStyleList };
        });
        const textBodyStyle = this.editorContainer.editor
            ? EditorDataModel.convertTextBodyStyle(this.editorContainer.getEditorValue(), false)
            : this.editorModel.getTextBodyStyle();
        return { textBodyStyle, paraStyleList };
    };

    private mergeStyles = (paraStyleList: ParaRunStyles[], textBodyStyle: ITextBodyStyle) => {
        if (!paraStyleList?.length) return {};

        // 给paraStyle添加默认的textAlign，character，lineHeight属性值
        const runCombineStyles: any[] = [];
        paraStyleList.forEach((style: ParaRunStyles) => {
            const p: IParagraphStyle = {
                textAlign: 'left',
                PPTLineHeight: 1,
                lineHeight: 1.2,
                ...style.paraStyle,
            };

            if (!p.character) {
                p.buNone = 1;
            }

            const { runStyleList } = style;
            Array.isArray(runStyleList) && runStyleList.forEach((runStyle: ITextRunStyle) => {
                const combineStyle = this.fixCombineStyle(p, runStyle, textBodyStyle);
                runCombineStyles.push(combineStyle);
            });
        });

        return stylesMerge(runCombineStyles);
    };

    private fixCombineStyle = (p, runStyle, textBodyStyle) => {
        let combineStyle = {
            ...p,
            ...runStyle,
            alignItems: 'flex-start',
            ...textBodyStyle?.wrapperStyle,
            ...textBodyStyle?.bodyStyle,
        };
        // 段落的段前/段后间距和文本框的文字边距属性 key 重复了 （都叫 paddingTop 和 paddingBottom）
        // 这里给段前/段后间距改个名字
        combineStyle.paraPaddingTop = p.paddingTop;
        combineStyle.paraPaddingBottom = p.paddingBottom;
        if (combineStyle.fontSize) {
            combineStyle.fontSize /= FONT_SCALE;
        }
        // 转换出对应的自定义项目类型
        if (combineStyle.character) {
            combineStyle = {
                ...combineStyle,
                ...combineStyle.character,
            };
            combineStyle.character = hasBullet(combineStyle);
        }
        // 文本填充
        combineStyle[FONT_FILL_KEY] = {
            [FillType.NoFill]: combineStyle.noFill,
            [FillType.PattFill]: combineStyle.pattFill,
            [FillType.GradualFill]: combineStyle.gradFill,
            [FillType.ImageFill]: combineStyle.image,
            [FillType.SolidFill]: combineStyle.color,
        };
        return combineStyle;
    };

    // 这个接口用于直接获取editor上的最新value
    // editorModel上不一定是最新的数据，所以在editor上拿最新的数据
    private getEditorData = (): { paragraphList: IParagraph[]; textBodyStyle?: ITextBodyStyle } => {
        let p;
        let t;
        const documentElement = this.editorContainer?.getEditorValue();
        if (documentElement) {
            const {
                paragraphList,
                textBodyStyle,
            } = this.editorModel.doConvertFromDocElementKeepInheritData(documentElement, false, false);
            p = paragraphList;
            t = textBodyStyle;
        }

        if (!p || (p && !p.length)) { // 无段落属性，返回默认值
            p = [{
                paragraphProps: {},
                textRuns: [{ text: '', styles: {} }],
            }];
        }
        if (!t) {
            t = {};
        }
        return {
            paragraphList: p,
            textBodyStyle: t,
        };
    };

    private handleLinkMouseEnter = (e: InteractionEvent): void => {
        // 预览状态下触发的链接的handleMouseEnter
        const option = {
            editable: this.props.editable,
            modelKey: this.props.modelKey,
            selected: this.props.selected || this.props.getSelected?.(),
        };
        this.services.linkManager?.handleLinkMouseEnter(
            e,
            {
                editor: this.editorContainer.editor ?? undefined,
                updateDataCB: () => {
                    this.updateEditorData();
                },
            },
            option,
        );
    };

    private handleLinkClick = (e: InteractionEvent): void => {
        this.handleLinkMouseEnter(e);
    };

    private handleLinkMouseLeave = (e: InteractionEvent): void => {
        // 预览状态下触发的链接的handleMouseLeave
        const option = {
            selected: this.props.selected || this.props.getSelected?.(),
        };
        this.services.linkManager?.handleLinkMouseLeave(e, option);
    };

    private handleLinkTouchEnd = (e: InteractionEvent): void => {
        this.services.linkManager?.showLinkFloatBarMobile(e, this.editorContainer.editor ?? undefined);
    };

    private getLinkColor = (textElement: TextElement): string => {
        if (textElement['origin-href']?.colorType === 'tx' && textElement.color) {
            return textElement.color;
        }

        if (this.props.linkColor) {
            return this.props.linkColor;
        }

        return DEFAULT_LINK_COLOR;
    };

    private onLinkClick = (e: InteractionEvent) => {
        if (this.props.openLinkInPlay) {
            const { textElement } = e.currentTarget as LinkText || {};
            this.props.openLinkInPlay(textElement?.['origin-href']);
        } else {
            this.handleLinkClick(e);
        }
    };

    private onLinkTouchEnd = (e: InteractionEvent) => {
        if (this.props.openLinkInPlay) {
            const { textElement } = e.currentTarget as LinkText || {};
            this.props.openLinkInPlay(textElement?.['origin-href']);
        } else {
            this.handleLinkTouchEnd(e);
        }
    };

    private getCursor = (cursor: string = TEXT_CURSOR) => {
        if (this.editorModel.isVert && cursor === TEXT_CURSOR) {
            return VERTICAL_TEXT_CURSOR;
        }
        return cursor;
    };

    /**
     * 选区或光标位置变更回调
     */
    private handleSelectionChange = (prevSelection: Selection, currentSelection: Selection) => {
        this.controller?.handleSelectionChange(prevSelection, currentSelection);
        this.props.handleSelectionChange?.();
    };

    private onInit = () => {
        if (this.props.modelKey) {
            this.services.fontManager?.registerOnFontDownloadCallback(
                this.props.modelKey,
                this.onFontDownloadedCallback,
            );
        }
    };

    private handleBeforeLayout = (editorValue: DocumentElement): DocumentElement => {
        let resultValue = editorValue;
        resultValue = this.changeParagraphCharacter(editorValue);
        if (this.props.styleRenderCallback) {
            return this.filterStyles(editorValue, this.props.styleRenderCallback);
        }
        return resultValue;
    };

    private filterStyles = (editorValue, styleRenderCallback): DocumentElement => {
        let documentParagraphList = editorValue.children;
        documentParagraphList = documentParagraphList?.map((paragraph) => {
            const charFontType = paragraph['origin-character']?.buFont?.typeface;
            const charType = paragraph['origin-character']?.character_bullet_char;
            paragraph = styleRenderCallback(paragraph, 'paragraph');
            if (paragraph.characterProps) {
                paragraph.characterProps = styleRenderCallback({ ...paragraph.characterProps, font: charFontType, type: charType }, 'character');
            }
            paragraph.children = paragraph.children.map(textRun => styleRenderCallback(textRun, 'textRun'));
            return paragraph;
        });
        const tempEditorValue = styleRenderCallback(editorValue, 'body');
        return {
            ...tempEditorValue, children: documentParagraphList,
        };
    };

    /**
     * 将项目符号透明度统一到changeParagraphCharacter中处理
     * @param editorValue
     * @returns
     */
    private changeParagraphCharacter = (editorValue: DocumentElement): DocumentElement => {
        const documentParagraphList = editorValue.children;
        const paragraphList = documentParagraphList.map(transformParagraphForCharacter);

        let startParaIndex = -1;
        const selectedParas = this.editor?.getSelectionNodes(node => node.type === 'paragraph');
        if (selectedParas?.length) {
            const startParaPath = selectedParas?.[0]?.[1];
            startParaIndex = startParaPath?.[startParaPath.length - 1];
        }

        let hasCharacter = false;
        const newDocumentParagraphList = editorValue.children.map((paragraph, index) => {
            const characterProps = createCharacterProps(
                paragraphList[index],
                index,
                paragraphList,
                startParaIndex,
                this.editor?.isEditing,
            );
            if (characterProps) {
                hasCharacter = true;
            }
            return {
                ...paragraph,
                characterProps,
            };
        });
        if (hasCharacter) {
            return {
                ...editorValue,
                children: newDocumentParagraphList,
            };
        }
        return editorValue;
    };

    private get controller() {
        return this.eventHandlers?.getEditorController();
    }

    /**
     * 获取当前所在平台
     * @returns
     */
    private getPlatform() {
        if (browser.isIOS) {
            return Platform.iosMobile;
        }
        if (browser.isAndroid) {
            return Platform.androidMobile;
        }
        return Platform.pc;
    }

    private getEditorProps = (props: IEditorProps) => ({
        ...props,
        id: this.id,
        rectTextId: CANVAS_RECT_TEXT_ID,
        textImgId: CANVAS_TEXT_IMAGE_ID,
        defaultFontFamily: DEFAULT_FONT_FAMILY,
        specificFontFamilies: SPECIFIC_DEFAULT_FONT_FAMILY,
        platform: this.getPlatform(),
        preventScrollOnFocus: browser.isMobile,
        supportMouseOnTouchScreen: browser.isPad,
        rect: this.getTextRect(this.props.textRect),
        readonly: this.isReadOnly(),
        value: this.getEditorValue(),
        noDefaultEvent: true,
        needCustomRender: this.linkHandler.isLinkText,
        getCustomNode: this.linkHandler.getLinkNode,
        getTextDirection: () => this.editorModel.textDirection,
        cursor: this.getCursor(this.props.cursor),
        handleKeyDown: (e: KeyboardEvent) => !!this.eventHandlers?.handleKeyDown(e),
        onFocus: () => {
            this.eventHandlers?.handleEditorFocus();
        },
        onBlur: () => {
            this.eventHandlers?.handleEditorBlur();
        },
        getBackDeleteTextProps: this.getBackDeleteTextProps,
        onValueChange: () => {
            this.controller?.handleValueChange();
        },
        onCompositionInput: () => {
            this.controller?.handleCompositionInput();
        },
        getAppPosition: this.getAppPosition,
        handleBeforeInput: (text: string, textElement: TextElement) =>
            this.eventHandlers?.handleBeforeInput(text, textElement),
        handleSelectionChange: this.handleSelectionChange,
        onInit: this.onInit,
        handleBeforeLayout: this.handleBeforeLayout,
        onDispose: this.dispose,
        noExtendProps: [
            { prop: 'origin-fieldType', preNoExtend: true, afterNoExtend: true },
            { prop: 'href', preNoExtend: true, afterNoExtend: true },
            { prop: 'origin-href', preNoExtend: true, afterNoExtend: true },
            { prop: 'origin-raw-href', preNoExtend: true, afterNoExtend: true },
            { prop: 'inlineProps', preNoExtend: true, afterNoExtend: true },
        ],
    });
}
